import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useTheme } from 'styled-components';
import { useRouter } from 'next/router';

import Image from 'components/common/Image';
import EmptyUserProfilePhoto from 'components/EmptyUserProfilePhoto/EmptyUserProfilePhoto';
import {
  AmbasadorIcon,
  ChatIcon,
  DonorIcon,
  ElipsisVertical,
  LockIcon,
  SendIcon,
  VerifiedIcon,
} from 'public/assets/icons';
import {
  actionTypes,
  openSideBar,
  openSidebarChatConversation,
  openSidebarInvolvedUsers,
} from 'store/sideBarControler/actions';
import { InvolvedUserTypes } from 'utils/constants';
import Button from 'components/common/Button';
import { anonymousClickHandler } from 'utils/anonymousClickHandler';
import { createSingleChatStart } from 'store/chat/actions';
import Dropdown from 'components/Dropdown';
import { routesPath } from 'utils/constants/routePath';
import { ConfirmationModal } from 'components/Modals';
import { reportUserStart } from 'store/user/actions';
import imageResize from 'utils/imageResize';
import appState from 'hooks/appState';
import getMaxString from 'utils/getMaxString';
import { APP_STATE } from 'types';
import { IProfileDetailsCard } from './IProfileDetailsCard';

import {
  ButtonWrapper,
  CentralizeContent,
  CountText,
  CoverImage,
  DotButton,
  FollowingInfoWrap,
  IconWrapper,
  MessageButtonWrap,
  NearbyBlock,
  OptionsButton,
  PhotoWithNearbyWrap,
  ProfileDetailsCardWrapper,
  ShadowGradient,
  TextWrap,
  UserDetailsWrap,
  UserInfo,
  UsernameText,
  UserProfilePhotoWrap,
} from './ProfileDetailsCard.styles';

const ProfileDetailsCard = ({
  id,
  coverPhoto,
  profilePhoto,
  isNearby,
  isMe,
  username,
  isVerified,
  isAmbassador,
  isDonor,
  fullName,
  followersCount = 0,
  followingCount = 0,
  followingStatus,
  postsCount = 0,
  onBlockUser,
  onClick,
  onEditProfile,
  description,
}: IProfileDetailsCard) => {
  const [copiedStatus, setCopiedStatus] = useState<boolean>(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
    useState<boolean>(false);
  const [isUserBlocked, setIsUserBlocked] = useState<boolean>(false);

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme: any = useTheme();
  const router = useRouter();
  const appStateAction = appState();

  const textOrangeButton = () => {
    if (isMe) {
      return t('editUserProfile:editProfileUppercase');
    }

    if (followingStatus === 0) {
      return t('group:followUppercase');
    }

    if (followingStatus === 1) {
      return t('group:requestedUppercase');
    }

    if (followingStatus === 2) {
      return t('group:followingUppercase');
    }
  };

  const onUserBlock = (userId) => {
    setIsUserBlocked(true);
    onBlockUser(userId);
  };

  const urlCopyToClipboard = () => {
    setCopiedStatus(true);
    const url = window.location.href;
    navigator.clipboard.writeText(url);
  };

  const onReport = (reportData) => {
    dispatch(reportUserStart({ userId: id, reportData }));
    setIsConfirmationModalOpen(false);
  };

  const isMeListItems = [
    {
      id: 1,
      text: copiedStatus ? t('common:urlCopied') : t('common:copyProfileUrl'),
      onClick: () => urlCopyToClipboard(),
    },
    {
      id: 2,
      text: t('profile:settings'),
      onClick: () =>
        anonymousClickHandler(() => router.push(`${routesPath.settings}`)),
    },
  ];

  const notIsMeListItems = [
    {
      id: 1,
      text: copiedStatus ? t('common:urlCopied') : t('common:copyProfileUrl'),
      onClick: () => urlCopyToClipboard(),
    },
    {
      id: 2,
      text: t('profile:inviteToGroup'),
      onClick: () =>
        anonymousClickHandler(() =>
          dispatch(openSideBar(actionTypes.OPEN_SIDEBAR_ADD_USER_GROUP))
        ),
    },
    {
      id: 3,
      text: t('profile:report'),
      onClick: () =>
        anonymousClickHandler(() => setIsConfirmationModalOpen(true)),
    },
    {
      id: 4,
      text: isUserBlocked ? t('profile:userBlocked') : t('profile:blockUser'),
      onClick: () => anonymousClickHandler(() => onUserBlock(id)),
    },
  ];

  return (
    <>
      <ProfileDetailsCardWrapper>
        <CoverImage>
          <Image src={imageResize(coverPhoto.trim())} alt="" layout="fill" />
          <ShadowGradient />
        </CoverImage>
        <UserInfo>
          <PhotoWithNearbyWrap>
            <UserProfilePhotoWrap>
              {profilePhoto?.trim()?.length ? (
                <Image
                  src={
                    imageResize(profilePhoto, '_thumb') || profilePhoto?.trim()
                  }
                  alt={getMaxString(username || description, 8)}
                  layout="fill"
                  unoptimized
                  priority
                />
              ) : (
                <EmptyUserProfilePhoto
                  wrapSize={80}
                  userIconSize={40}
                  withBorder={false}
                />
              )}
            </UserProfilePhotoWrap>
            {isNearby && !isMe && (
              <NearbyBlock>
                <SendIcon />
                <div>{t('profile:nearby')}</div>
              </NearbyBlock>
            )}
          </PhotoWithNearbyWrap>
          <UserDetailsWrap isNearby={isNearby && !isMe}>
            <CentralizeContent mb={2}>
              <UsernameText type="h1">{username}</UsernameText>
              {isVerified && (
                <IconWrapper>
                  <VerifiedIcon />
                </IconWrapper>
              )}
              {isAmbassador && (
                <IconWrapper>
                  <AmbasadorIcon />
                </IconWrapper>
              )}
              {isDonor && (
                <IconWrapper>
                  <DonorIcon />
                </IconWrapper>
              )}
            </CentralizeContent>
            <TextWrap>{fullName}</TextWrap>
            <FollowingInfoWrap>
              <CentralizeContent
                isClickable
                mr={23}
                onClick={() => {
                  dispatch(
                    openSidebarInvolvedUsers({
                      id,
                      involvedUserType: isMe
                        ? InvolvedUserTypes.MY_FOLLOWERS
                        : InvolvedUserTypes.USER_FOLLOWERS,
                    })
                  );
                }}
              >
                <CountText>{`${followersCount && followersCount}`}</CountText>
                <TextWrap>{t('profile:followers')}</TextWrap>
              </CentralizeContent>
              <CentralizeContent
                isClickable
                onClick={() => {
                  dispatch(
                    openSidebarInvolvedUsers({
                      id,
                      involvedUserType: isMe
                        ? InvolvedUserTypes.MY_FOLLOWING
                        : InvolvedUserTypes.USER_FOLLOWING,
                    })
                  );
                }}
              >
                <CountText>{`${followingCount && followingCount}`}</CountText>
                <TextWrap>{t('profile:following')}</TextWrap>
              </CentralizeContent>
              {/* <CentralizeContent ml={23} isMobileHidden>
                <CountText>{postsCount}</CountText>
                <TextWrap>{t('feed:posts')}</TextWrap>
              </CentralizeContent> */}
            </FollowingInfoWrap>
          </UserDetailsWrap>
        </UserInfo>
        <ButtonWrapper>
          <Button variant="primary" onClick={onClick}>
            {textOrangeButton()}
          </Button>
          {!isMe && (
            <MessageButtonWrap>
              <Button
                buttonType="icon"
                bgColor={theme.colors.neutral.n3}
                onClick={() =>
                  anonymousClickHandler(() => {
                    dispatch(createSingleChatStart(id));
                    dispatch(openSidebarChatConversation({ chatId: id }));
                  })
                }
              >
                <ChatIcon />
              </Button>
            </MessageButtonWrap>
          )}
          <OptionsButton>
            <Dropdown listItems={isMe ? isMeListItems : notIsMeListItems}>
              <ElipsisVertical />
            </Dropdown>
          </OptionsButton>
        </ButtonWrapper>
      </ProfileDetailsCardWrapper>
      {isConfirmationModalOpen && (
        <ConfirmationModal
          headerText={t('common:reportHeader')}
          submitText={t('settings:submitBtnUppercase')}
          onSubmit={() => alert('Submit')}
          loading={false}
          onCancelClick={() => setIsConfirmationModalOpen(false)}
          isReport
          onReportSubmit={onReport}
        />
      )}
    </>
  );
};

export default ProfileDetailsCard;
